import {
  CalendarDate,
  CalendarDateTime,
  getDayOfWeek,
  getLocalTimeZone,
  Time,
  toCalendarDate,
  toCalendarDateTime,
  toTime,
  toZoned,
} from "@internationalized/date";
import { WeekRepeat } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/PeriodicDuration";
import { Rule } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { PeriodRequestDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/requestSchema";
import {
  PeriodicDuration,
  PeriodResponseDuration,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";

export const getDatesFromWeekdays = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  weekdays: number[],
  repeat: WeekRepeat,
) => {
  // finner ukedagnummeret til startDate
  const startDay = getDayOfWeek(startDate, "nb-NO");

  const dates: CalendarDate[] = [];
  weekdays.forEach((weekday) => {
    // Finner første dato som matcher ønsket ukedag
    let currentDateMatchingWeekday = startDate.add({
      days: (weekday - startDay + 7) % 7,
    });

    // så lenge sluttdatoen ikke er passert, legg til currentDateMatchingWeekday og hopp en/to uke(r) fram
    while (currentDateMatchingWeekday.compare(endDate) <= 0) {
      dates.push(currentDateMatchingWeekday);
      currentDateMatchingWeekday = currentDateMatchingWeekday.add({
        days: repeat === WeekRepeat.WEEKLY ? 7 : 14,
      });
    }
  });
  return dates;
};

const createPeriods = (
  id: string,
  dates: CalendarDate[],
  startTime: Time,
  endTime: Time,
) => {
  const periods: PeriodRequestDuration[] = [];
  dates.forEach((date) => {
    const isPassingMidnight = endTime.compare(startTime) < 0;
    const fromDateTime = toCalendarDateTime(date, startTime);
    const toDateTime = isPassingMidnight
      ? toCalendarDateTime(date.add({ days: 1 }), endTime)
      : toCalendarDateTime(date, endTime);
    periods.push({
      id,
      fromTime: toZoned(fromDateTime, getLocalTimeZone()).toString(),
      toTime: toZoned(toDateTime, getLocalTimeZone()).toString(),
    });
  });
  return periods;
};

const getRuleFromPeriodWithId = (period: PeriodResponseDuration[]): Rule => {
  const sortedPeriods = period.sort((a, b) => a.from_time.compare(b.from_time));
  const startDateTime = sortedPeriods[0].from_time;
  const endDateTime = sortedPeriods[sortedPeriods.length - 1].to_time;

  const daysOfWeek: number[] = [];
  sortedPeriods.forEach((p) => {
    daysOfWeek.push(getDayOfWeek(p.from_time, "nb-NO"));
  });

  const uniqueDaysOfWeek = daysOfWeek.filter(
    (day, index, array) => array.indexOf(day) === index,
  );

  const getRepeatInterval = () => {
    const startDay = getDayOfWeek(startDateTime, "nb-NO");
    const nextOfSameWeekday = sortedPeriods.find((p, index) => {
      if (index > 0) {
        return getDayOfWeek(p.from_time, "nb-NO") === startDay;
      }
      return false;
    });
    const nextStartDateTime = nextOfSameWeekday?.from_time;

    const plusForteenDays = startDateTime.add({ days: 14 });
    const repeat =
      nextStartDateTime && plusForteenDays.compare(nextStartDateTime) === 0
        ? WeekRepeat.BIWEEKLY
        : WeekRepeat.WEEKLY;

    return repeat;
  };
  return {
    id: period[0].id,
    startDateTime,
    endDateTime,
    days: uniqueDaysOfWeek,
    repeat: getRepeatInterval(),
  };
};

const getRuleFromPeriodWithoutId = (
  startDateTime: CalendarDateTime,
  endDateTime: CalendarDateTime,
): Rule => ({
  id: Math.random().toString(16),
  startDateTime,
  endDateTime,
  days: [getDayOfWeek(startDateTime, "nb-NO")],
  repeat: WeekRepeat.WEEKLY,
});

export const createRulesFromPeriodicDuration = (duration: PeriodicDuration) => {
  const rulesFromPeriodsWithoutId: Rule[] = duration.periods
    .filter((period) => period.id === null)
    .map((p) => getRuleFromPeriodWithoutId(p.from_time, p.to_time));
  const periodsGroupedById: Map<string, PeriodResponseDuration[]> = new Map();

  duration.periods.forEach((period) => {
    if (period.id !== null) {
      if (periodsGroupedById.has(period.id)) {
        const prev = periodsGroupedById.get(period.id)!;
        periodsGroupedById.set(period.id, [
          ...prev,
          { ...period, id: period.id },
        ]);
      } else periodsGroupedById.set(period.id, [{ ...period, id: period.id }]);
    }
  });

  const rulesFromPeriodsWithId: Rule[] = [];

  periodsGroupedById.forEach((periodList) => {
    const rule = getRuleFromPeriodWithId(periodList);
    rulesFromPeriodsWithId.push(rule);
  });

  return [...rulesFromPeriodsWithoutId, ...rulesFromPeriodsWithId];
};

export const ruleToPeriod = (rule: Rule): PeriodRequestDuration[] => {
  const datesFromWeekdays = getDatesFromWeekdays(
    toCalendarDate(rule.startDateTime),
    toCalendarDate(rule.endDateTime),
    rule.days,
    rule.repeat,
  );
  const period = createPeriods(
    rule.id,
    datesFromWeekdays,
    toTime(rule.startDateTime),
    toTime(rule.endDateTime),
  );

  return period;
};
